import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { useWorkspaceDock } from './workspace/WorkspaceDock';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../redux/actions/authActions';
import { listMyChallengeGroups, leaveChallengeGroup } from '../services/livesqlRepo';
import { loadChallengeList } from '../redux/actions/challengeActions';
// 메뉴 클릭은 handleMenuClick 한 곳을 지난다 — 기록도 거기서 한 번만 한다
import { logAction, downloadDiagnostics } from '../services/opsLog';
import { publishMenuActions, registerMenuRunner } from '../utils/menuActionBus';
import { isProduction } from '../utils/connectionTypes';
import buildInfo from '../build-info.json';
import { useConfirm } from './ConfirmDialog';
import { formatApiError } from '../utils/formatters';
import NicknameChangeModal from './NicknameChangeModal';
import PasswordChangeModal from './PasswordChangeModal';
import WithdrawalModal from './WithdrawalModal';
import ParticipationGroupModal from './ParticipationGroupModal';
import MonitoringDashboardModal from './monitoring/MonitoringDashboardModal';
import OraclePerfAnalysisModal from './monitoring/OraclePerfAnalysisModal';
import MonitoringCriteriaModal from './monitoring/MonitoringCriteriaModal';
import QueryTemplateManagerModal from './monitoring/QueryTemplateManagerModal';
import DynamicViewExplorerModal from './monitoring/DynamicViewExplorerModal';
import ResourceAnalysisModal from './monitoring/ResourceAnalysisModal';
import PlsqlEditorModal from './monitoring/PlsqlEditorModal';
import SessionAdminModal from './monitoring/SessionAdminModal';
import LockWaitModal from './monitoring/LockWaitModal';
import OracleUtilModal from './OracleUtilModal';
import SessionSettingsModal from './SessionSettingsModal';
import LogViewerModal from './LogViewerModal';
import { openFeedback } from './FeedbackDialog';
import { AI_ENABLED, IS_STANDALONE } from '../config/features';
import useUpdateCheck from '../hooks/useUpdateCheck';
import useOpsLogAlerts from '../hooks/useOpsLogAlerts';
import useEvolutionNotices from '../hooks/useEvolutionNotices';
import useMonitoringAccess, { usesExpertMenus } from '../hooks/useMonitoringAccess';
import useFeedbackReplies from '../hooks/useFeedbackReplies';
import { toast } from 'react-toastify';
import { loadLayout, saveLayout } from '../utils/workspaceLayout';
import { useTranslation } from 'react-i18next';
import i18n, { SUPPORTED_LANGUAGES, setLanguage } from '../i18n';
import styles from './Header.module.css';
import {
    FiFile, FiFilePlus, FiFolder, FiSave,
    FiEdit3, FiSearch, FiCheckSquare, FiColumns,
    FiPlay, FiPlayCircle, FiClock, FiBarChart2, FiDatabase,
    FiAward, FiZap,
    FiInfo, FiUser, FiLogOut, FiEdit, FiKey, FiAlertTriangle,
    FiUsers, FiUserPlus, FiUserMinus, FiSettings,
    FiAlignLeft, FiHash, FiType, FiEye, FiTool, FiCheck, FiRefreshCw,
    FiActivity, FiHardDrive, FiCode, FiUserX, FiBookOpen, FiMessageSquare, FiFileText, FiFlag, FiDownload,
    FiBookmark, FiCheckCircle, FiXCircle, FiChevronRight, FiLock, FiShield, FiTrendingUp, FiLayers, FiStar, FiPieChart,
} from 'react-icons/fi';
import HelpGuideModal from './HelpGuideModal';
import { openGuardrail } from './guardrail/GuardrailHost';
import { findOwnConnSession } from '../utils/sessionDisplay';
import { ORACLE_CONN, ORACLE_OR_PG_CONN } from '../hooks/useOwnConnSession';
import OwnConnChooser from './monitoring/OwnConnChooser';
import { usePerfJump } from '../utils/perfJump';

import { toCssPx } from '../utils/appZoom';
import memoWindow from './common/memoWindow';
/**
 * 드롭다운 구분선. 렌더러가 action === 'separator' 로만 판별하고 이름은 쓰지 않는다.
 * 예전엔 항목마다 한국어 이름('구분선')을 달아 i18n 가드에 하드코딩 한글로 잡혔다 —
 * 쓰이지 않는 이름이라 아예 두지 않는다.
 */
const MENU_SEPARATOR = { action: 'separator' };

/**
 * 메뉴 `action` id → 사용로그 액션 id.
 *
 * 메뉴 id 를 그대로 쓰지 않는 이유: 로그 쪽 목록은 **서버가 가진 화이트리스트**라 메뉴 사정으로
 * 바뀌면 안 되고(집계가 갈린다), 메뉴에는 로그로 남길 가치가 없는 것(구분선·창 정렬 등)도 있다.
 * 여기 없는 메뉴는 그냥 기록되지 않는다.
 */
const MENU_LOG_ACTION = {
    'monitoring-dashboard': 'modal.monitoringDashboard',
    'monitoring-settings': 'modal.monitoringAnalysis',
    'monitoring-oracle-perf': 'modal.sqlPerf',
    'dynamic-view-explorer': 'modal.dynamicView',
    'resource-analysis': 'modal.resourceAnalysis',
    'session-admin': 'modal.sessionAdmin',
    'app-settings': 'modal.settings',
    'help-guide': 'modal.help',
    challengejoin: 'modal.challenge',
    // 편집기·파일 조작도 화면에서 끝나는 기능이라 서버가 모른다
    new: 'editor.newFile',
    open: 'editor.openFile',
    save: 'editor.saveFile',
    viewschema: 'explorer.tableInfo',
    'global-find': 'explorer.search',
    format: 'editor.format',
    // 도구 메뉴 — 툴바 버튼(AssistantToolButtons)과 같은 id 로 센다
    'tool-plan-archive': 'modal.planArchive',
    'tool-sql-tuning': 'modal.sqlTuning',
    'tool-sql-review': 'modal.sqlReview',
    'tool-sql-builder': 'modal.sqlBuilder',
    'view-analysis': 'modal.dataAnalysis',
    'tool-sql-author': 'modal.sqlAuthor',
    'tool-sql-quality': 'modal.sqlQuality',
    'tool-schema-compare': 'modal.schemaCompare',
    'tool-sql-collect': 'modal.sqlCollect',
    'tool-tuning-cases': 'modal.tuningCases',
    'tool-table-analysis': 'modal.tableAnalysis',
    'tool-index-design': 'modal.indexDesign',
    'tool-partition-design': 'modal.partitionDesign',
    'tool-design-guides': 'modal.designGuides',
};

/** 목록에 있는 메뉴만 기록한다. 로그가 화면을 막지 않도록 실패는 services/opsLog 가 삼킨다. */
function logMenuAction(menuAction) {
    const id = MENU_LOG_ACTION[menuAction];
    if (id) logAction(id);
}

const Header = ({ onSaveFile, onOpenFile, onNewFile, onSaveProject, onSaveProjectAs, onOpenSampleProject, currentProjectName, onGlobalSearch, onExecuteAll, onExecuteCurrent, onExecutePlan, onViewExecutionHistory, onViewSchemaInfo, onCommit, onRollback, isEditorFocused, onChallengeJoin, onChallengeTuning, onChallengeStatus, screenZoom, onZoomChange, onEditorFind, onEditorReplace, onEditorFormat, onEditorUppercase, onEditorLowercase, onEditorToggleComment, viewState = {}, onToggleView, onOpenCaseList, onOpenTableAnalysis, onOpenDesignGuides, onOpenSqlTuning, onOpenSqlReview, onOpenSqlAuthor, onOpenSqlQuality, onOpenSchemaCompare, onOpenSqlCollect, onOpenSqlBuilder, onOpenDataAnalysis, onOpenPlanArchive, onOpenOpsLog, onOpenExperience, onOpenConnectionModal, isFavoriteAction, onToggleFavorite, appActionKeys = [], darkMode, onToggleDarkMode, onOpenSettings, onOpenSqlPerfInEditor, activeConnectionId, activeConnectionName, activeSession, activeSessionConnection, hasActiveTab, unsavedTabCount = 0, onAskAssistant }) => {
    const { t } = useTranslation();
    const [activeMenu, setActiveMenu] = useState(null);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [showNicknameChangeModal, setShowNicknameChangeModal] = useState(false);
    const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
    const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
    const [showParticipationGroupModal, setShowParticipationGroupModal] = useState(false);
    const [participationGroupMenuOpen, setParticipationGroupMenuOpen] = useState(false);
    // 내가 가입한 참여그룹(서버, myChallengeGroups) · 지금 쓰는 그룹(제출 기록의 그룹 — 이 브라우저가 기억하는 「선택」 일 뿐, 가입은 서버)
    const [myGroups, setMyGroups] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [showAboutModal, setShowAboutModal] = useState(false);
    const [showHelpGuide, setShowHelpGuide] = useState(false);
    // Oracle 네이티브 유틸리티(exp/imp/expdp/impdp) 모달
    const [showOracleUtil, setShowOracleUtil] = useState(false);
    // 세션 설정(자동커밋·격리수준·client info·ALTER SESSION) 모달
    const [showSessionSettings, setShowSessionSettings] = useState(false);
    // 진단 로그 보기 — 스탠드얼론은 콘솔이 없어 로그가 유일한 단서다.
    const [showLogViewer, setShowLogViewer] = useState(false);
    const [showMonitoringDashboard, setShowMonitoringDashboard] = useState(false);
    const [showOraclePerfAnalysis, setShowOraclePerfAnalysis] = useState(false);
    const [showMonitoringCriteria, setShowMonitoringCriteria] = useState(false);
    // 쿼리 템플릿 → 수집 기준 등록(2026-10-28) — 템플릿 창이 넘긴 초안. nonce 가 바뀔 때마다 수집 설정 창이 새 사용자 기준으로 채운다
    const [criteriaSeed, setCriteriaSeed] = useState(null);
    const [showQueryTemplateManager, setShowQueryTemplateManager] = useState(false);
    const [showDynamicViewExplorer, setShowDynamicViewExplorer] = useState(false);
    const [showResourceAnalysis, setShowResourceAnalysis] = useState(false);
    const [showPlsqlEditor, setShowPlsqlEditor] = useState(false);
    const [showSessionAdmin, setShowSessionAdmin] = useState(false);
    const [showLockWait, setShowLockWait] = useState(false);
    // 독립 연결 창마다 「이번에 열 연결」 — 창의 전담 세션이 있던 연결이거나 연결 선택 창에서 고른 것
    const [windowConn, setWindowConn] = useState({});
    // 연결 선택 창 — 그 창의 전담 세션이 없을 때만 뜬다 { key }
    const [connChooser, setConnChooser] = useState(null);
    const menuRef = useRef(null);
    /**
     * 머리줄 압축(2026-10-10) — 폭이 모자라면 단계적으로 줄인다. 화면 확대(utils/appZoom)를 올리면 같은 창에서도 CSS 폭이
     * 줄어 넘치므로(1600px 창에서 100% 도 41px 넘쳤다) 넘칠 때만 한 단계씩 올린다:
     *   1 간격·이름 폭 줄임 · 연결 이름 숨김 → 2 메뉴는 아이콘만(이름은 툴팁) → 3 그룹·사용자 이름·세션 표시·100% 단추 숨김
     * CSS 컨테이너 쿼리를 쓰지 않는 까닭: 이 <header> 안에 창(모달)들이 그려지는데, container-type 은 그 안의 position:fixed 를
     * 머리줄에 가둔다. 그래서 넘침을 직접 재서 data-compact 를 단다(렌더 없이 DOM 속성만 — 다시 그리기 고리를 만들지 않는다).
     */
    const headerRef = useRef(null);
    const fitHeader = useCallback(() => {
        const el = headerRef.current;
        if (!el) return;
        let level = 0;
        el.dataset.compact = '0';
        while (level < 3 && el.scrollWidth > el.clientWidth + 1) {
            level += 1;
            el.dataset.compact = String(level);
        }
        // 메뉴 이름이 숨으면 그 이름을 툴팁으로 — 보일 때 같은 글자를 또 띄우지 않는다
        el.querySelectorAll('[data-menu-name]').forEach((b) => {
            if (level >= 2) b.setAttribute('title', b.getAttribute('data-menu-name'));
            else b.removeAttribute('title');
        });
    }, []);
    useEffect(() => {
        const el = headerRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(() => fitHeader());
        ro.observe(el);
        return () => ro.disconnect();
    }, [fitHeader]);
    const userMenuRef = useRef(null);
    const participationGroupMenuRef = useRef(null);
    
    const dispatch = useDispatch();
    const { user } = useSelector(state => state.auth);
    const connections = useSelector(state => state.schema?.connections || []);
    const sessionsList = useSelector(state => state.sessions?.list || []);

    // ── 독립 연결 창 — 열기 전에 어느 연결로 볼지 정한다 (2026-10-01; 2026-10-31 「모니터링 창」 에서 이름을 바꿨다 — 메뉴는 DB 전문가 › · 작성 › 에 있다) ──
    // 창마다 그 창만 쓰는 DB 세션(전담 세션)으로 대상 DB 에 직접 조회한다. 메뉴 문구의 번역 키(menu:monitoring.*)는 처음 놓였던 메뉴의 이름이 남은 것이다
    // 창마다 전담 세션을 갖고 창을 닫아도 남겨 둔다(hooks/useOwnConnSession). 그래서
    //  - 그 창의 세션이 이미 있으면 **묻지 않고** 그 연결로 연다(편집기 연결의 것을 먼저 본다)
    //  - 없으면 연결 선택 창을 띄운다 — 기본은 편집기가 쓰는 연결. 고를 것이 하나뿐이면 묻지 않는다.
    // 키는 창의 label(utils/sessionDisplay 의 OWN_CONN_WINDOW_KEYS)과 같아야 재사용이 된다.
    const editorConnId = (activeSessionConnection && activeSessionConnection.id) || activeConnectionId || null;
    const OWN_CONN_WINDOWS = {
        'oracle-perf':       { filter: ORACLE_CONN,       open: setShowOraclePerfAnalysis, titleKey: 'menu:monitoring.oraclePerf',       icon: <FiBarChart2 size={14} /> },
        'dbms-catalog':      { filter: ORACLE_OR_PG_CONN, open: setShowDynamicViewExplorer, titleKey: 'menu:monitoring.dbmsCatalog',     icon: <FiDatabase size={14} /> },
        'resource-analysis': { filter: ORACLE_CONN,       open: setShowResourceAnalysis,   titleKey: 'menu:monitoring.resourceAnalysis', icon: <FiHardDrive size={14} /> },
        'plsql-editor':      { filter: ORACLE_CONN,       open: setShowPlsqlEditor,        titleKey: 'menu:monitoring.plsqlEditor',      icon: <FiCode size={14} /> },
        'session-admin':     { filter: ORACLE_CONN,       open: setShowSessionAdmin,       titleKey: 'menu:monitoring.sessionAdmin',     icon: <FiUserX size={14} /> },
        'lock-wait':         { filter: ORACLE_CONN,       open: setShowLockWait,           titleKey: 'menu:monitoring.lockWait',         icon: <FiLock size={14} /> },
    };
    const openOwnConnWindow = (key) => {
        const w = OWN_CONN_WINDOWS[key];
        // beginner 는 모니터링·DB 전문가 창을 열지 않는다(PL/SQL 편집기는 작성 메뉴라 연다 — 2026-10-29 사용자 유형)
        if (!w || (key !== 'plsql-editor' && !usesExpertMenus(monAccess))) return;
        const cands = connections.filter(w.filter);
        const usable = (s) => s && cands.some(c => c.id === s.connectionId);
        const mine = [findOwnConnSession(sessionsList, key, editorConnId), findOwnConnSession(sessionsList, key, null)]
            .find(usable);
        if (mine) {
            setWindowConn(m => ({ ...m, [key]: mine.connectionId }));
            w.open(true);
            return;
        }
        if (cands.length <= 1) {
            setWindowConn(m => ({ ...m, [key]: cands[0] ? cands[0].id : null }));
            w.open(true);
            return;
        }
        setConnChooser({ key });
    };
    const chooserWindow = connChooser ? OWN_CONN_WINDOWS[connChooser.key] : null;
    const isStandalone = IS_STANDALONE;
    // 관리자 알림 — 새 오류·재발·새 신고(화면 오류 수집 3단계). 권한은 서버가 판정하고, 아니면 0 이다
    const isAuthenticated = useSelector(state => !!(state.auth && state.auth.isAuthenticated));
    const logAlerts = useOpsLogAlerts(!isStandalone && isAuthenticated);
    // 진화 기록의 알림(기준 제안 · 가이드가 멈춘 학습) — 스탠드얼론에도 있다(로그인이 없다)
    const evoNotices = useEvolutionNotices(isStandalone || isAuthenticated);
    const monAccess = useMonitoringAccess(isStandalone || isAuthenticated, isStandalone);
    const canMonitor = monAccess.monitoring;
    // 사용자 유형(2026-10-29) — beginner 에게는 모니터링 › · DB 전문가 › 메뉴가 없다. 서버도 같은 판정으로 API 를 막는다
    const expertMenus = usesExpertMenus(monAccess);
    /**
     * 독립 연결 창의 열림을 기억하고 **새로고침 뒤에 그 연결로 다시 연다**(2026-10-31 사용자 결정 — 새로고침이니 묻지 않는다.
     * 10-12 에는 「대상 DB 에 곧바로 조회가 나간다」 며 알림으로 물었지만, 알림에는 이 창들만 나와 다른 작업 탭과 다르게 보였다).
     * 연결이 없어졌으면 고를 것이 하나뿐일 때만 열고, 여럿이면 열지 않는다 — 새로고침에 연결 선택 창을 띄우지 않는다.
     */
    const OWN_CONN_OPEN = {
        dashboard: showMonitoringDashboard, 'oracle-perf': showOraclePerfAnalysis, 'dbms-catalog': showDynamicViewExplorer,
        'resource-analysis': showResourceAnalysis, 'plsql-editor': showPlsqlEditor, 'session-admin': showSessionAdmin, 'lock-wait': showLockWait,
    };
    const ownConnOpenKeys = Object.keys(OWN_CONN_OPEN).filter(k => OWN_CONN_OPEN[k]).join(',');
    const windowConnJson = JSON.stringify(windowConn);
    // 다시 열기 전에는 저장하지 않는다 — 처음 그릴 때 「열린 창 없음」 으로 덮으면, 연결 목록·권한이 늦게 오거나
    // 머리줄이 다시 마운트될 때 열 기록이 이미 사라져 있다
    const [ownConnRestored, setOwnConnRestored] = useState(false);
    useEffect(() => {
        if (!ownConnRestored) return;
        saveLayout({ monitor: { open: ownConnOpenKeys ? ownConnOpenKeys.split(',') : [], conn: JSON.parse(windowConnJson) } });
    }, [ownConnOpenKeys, windowConnJson, ownConnRestored]);
    const reopenOwnConnWindows = (keys, conn) => {
        for (const key of keys) {
            if (key === 'dashboard') { setShowMonitoringDashboard(true); continue; }
            const w = OWN_CONN_WINDOWS[key];
            if (!w || (key !== 'plsql-editor' && !usesExpertMenus(monAccess))) continue;
            const cands = connections.filter(w.filter);
            const id = cands.some(c => c.id === conn[key]) ? conn[key] : (cands.length === 1 ? cands[0].id : null);
            if (!id) continue;
            setWindowConn(m => ({ ...m, [key]: id }));
            w.open(true);
        }
    };
    const reopenRef = useRef(reopenOwnConnWindows);
    reopenRef.current = reopenOwnConnWindows;
    const ownConnRestoredRef = useRef(false);
    useEffect(() => {
        // 로그인하고 연결 목록·권한(사용자 유형)이 온 뒤에 한 번 — 연결 목록은 브라우저 캐시(redux-persist)에 남아 로그인 화면에서도
        // 차 있어서, 로그인을 보지 않으면 로그인 전에 창을 열려고 했다(신고 2026-10-31). 기록은 사용자별이라 이때 읽는다.
        // 모니터링 권한은 실시간 대시보드에만 건다 — 조회 창(오라클 성능 분석 등)은 권한 없이 연다(2026-10-14 결정)
        if (ownConnRestoredRef.current || !connections.length || !(isStandalone || isAuthenticated) || !monAccess.ready) return;
        ownConnRestoredRef.current = true;
        setOwnConnRestored(true);
        const saved = loadLayout().monitor;
        const keys = saved.open.filter(k => (k === 'dashboard' ? canMonitor : !!OWN_CONN_WINDOWS[k]));
        if (keys.length) reopenRef.current(keys, saved.conn);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 연결·권한이 준비됐을 때 한 번
    }, [canMonitor, connections.length, isAuthenticated, monAccess.ready]);
    // 내 신고에 새 답 — 모든 사용자(관리자 알림과 별개)
    const feedbackReplies = useFeedbackReplies(!isStandalone && isAuthenticated);
    // 스탠드얼론 새 버전 — 알림만(hooks/useUpdateCheck.js). 새 판이면 도움말 메뉴와 정보 창이 말한다
    const { info: updateInfo, check: checkUpdate } = useUpdateCheck(isStandalone);
    const handleCheckUpdate = async () => {
        try {
            const r = await checkUpdate();
            if (r && r.newer) toast.info(t('menu:help.updateAvailable', { version: r.latest }));
            else toast.success(t('menu:help.updateLatest', { version: buildInfo.version }));
        } catch (err) {
            toast.error(t('menu:help.updateFailed', { message: err.message }));
        }
    };
    // 스탠드얼론 진단 파일 — 서버가 없어 기록이 이 PC 에만 있다. 파일로 받아 개발자에게 보낸다
    const handleExportDiagnostics = async () => {
        try {
            const name = await downloadDiagnostics();
            toast.success(t('menu:help.diagnosticsSaved', { name }));
        } catch (err) {
            toast.error(t('menu:help.diagnosticsFailed', { message: err.message }));
        }
    };

    // 탐색기가 보고 있는 연결. id 우선, 없으면 이름으로 찾는다(레거시 흐름 호환).
    const browseConn = connections.find(c => c.id === activeConnectionId)
        || connections.find(c => c.name === activeConnectionName)
        || null;

    /**
     * 상단 배지는 **실제로 SQL 이 나가는 곳**을 말한다.
     *
     * 탐색기 연결과 활성 탭 세션의 연결은 다를 수 있다 — 세션 선택이 "다른 연결의 세션" 도
     * 고르게 해 주기 때문이다. 예전에는 배지가 탐색기 연결만 보여 줘서, 배지에 "개발" 이라
     * 적힌 채 운영 세션으로 실행하는 일이 가능했다. 운영 경고 띠도 같은 값을 봐야 한다.
     *
     * 세션이 없으면 실행 자체가 막히므로(App 의 proceedWithExecution) 그 사실을 배지에 적는다.
     */
    const activeConn = activeSessionConnection || browseConn;
    const isProdConn = !!activeConn && isProduction(activeConn.connType);

    const allMenuItems = [
        {
            id: 'file',
            name: t('menu:file.title'),
            icon: <FiFile />,
            subItems: [
                { name: t('menu:file.new'), action: 'new', onClick: onNewFile, shortcut: 'Alt+N', icon: <FiFilePlus /> },
                { name: t('menu:file.open'), action: 'open', onClick: onOpenFile, shortcut: 'Ctrl+O', icon: <FiFolder /> },
                { name: t('menu:file.save'), action: 'save', onClick: onSaveFile, shortcut: 'Ctrl+S', icon: <FiSave /> },
                // 구분선은 action 으로만 판별하고 이름을 쓰지 않는다 — MENU_SEPARATOR 참고
                MENU_SEPARATOR,
                // 프로젝트(스크립트 그룹) — 여러 탭을 하나의 .sqlprj 로 묶어 저장/복원
                { name: t('menu:file.projectOpen'), action: 'project-open', onClick: onOpenFile, shortcut: 'Ctrl+Shift+O', icon: <FiFolder /> },
                { name: t('menu:file.projectSave'), action: 'project-save', onClick: onSaveProject, shortcut: 'Ctrl+Shift+S', icon: <FiSave /> },
                { name: t('menu:file.projectSaveAs'), action: 'project-saveas', onClick: onSaveProjectAs, icon: <FiSave /> },
                // 제품이 기본으로 주는 실습 프로젝트 — 앱에 동봉돼 있어 내부망에서도 그대로 열린다.
                // 사용자 저장물과 섞지 않는다: 여는 순간부터 보통 프로젝트와 똑같이 다룬다(고치고 따로 저장).
                { name: t('menu:file.sampleOpen'), action: 'sample-open', onClick: onOpenSampleProject, icon: <FiBookOpen /> },
                MENU_SEPARATOR,
                // 앱 전역 설정 (테마·글자 크기·에디터/그리드 옵션) — 연결 메뉴의 '세션 설정' 과는 층이 다르다
                { name: t('menu:file.appSettings'), action: 'app-settings', onClick: onOpenSettings, icon: <FiSettings />, opensWindow: true },
            ],
        },
        {
            id: 'connection',
            // 창을 여는 메뉴 — 즐겨찾기하면 툴바 오른편 도구 창 묶음에 **아이콘+레이블**로 붙는다(2026-09-13)
            opensWindow: true,
            name: t('menu:connection.title'),
            icon: <FiDatabase />,
            subItems: [
                { name: t('menu:connection.manage'), action: 'manage-connections', onClick: onOpenConnectionModal, icon: <FiDatabase /> },
                // Oracle 네이티브 유틸리티 — 결과 탭과 무관한 DB 도구라 연결 메뉴에 둔다
                { name: t('menu:connection.oracleUtil'), action: 'oracle-util', onClick: () => setShowOracleUtil(true), icon: <FiDatabase /> },
                { name: t('menu:connection.sessionSettings'), action: 'session-settings', onClick: () => setShowSessionSettings(true), icon: <FiSettings /> },
            ],
        },
        {
            id: 'edit',
            name: t('menu:edit.title'),
            icon: <FiEdit3 />,
            subItems: [
                // 「작성」 메뉴(2026-10-19 사용자 요청 — 옛 「편집」) — SQL 을 쓰는 도구를 맨 위에 모은다. 셋 다 DB 전문가에서 옮겨 왔다(두 곳에 두지 않는다).
                // action id 는 그대로라 즐겨찾기·툴바 단추는 이어진다. PL/SQL 편집기는 Oracle 전용(창이 연결을 거른다)
                { name: t('menu:tools.sqlAuthor'), action: 'tool-sql-author', onClick: onOpenSqlAuthor, icon: <FiEdit3 />, opensWindow: true },
                { name: t('menu:tools.sqlBuilder'), action: 'tool-sql-builder', onClick: onOpenSqlBuilder, icon: <FiLayers />, opensWindow: true },
                // 데이터 분석 — 분석 SQL 을 만들어 편집기에 넣는 작업 탭이라 작성 메뉴에 둔다(2026-10-28 사용자 요청 — 실행 › 에서 옮김). action id 는 그대로(즐겨찾기가 묶여 있다)
                { name: t('menu:edit.analysis'), action: 'view-analysis', onClick: onOpenDataAnalysis, icon: <FiPieChart />, opensWindow: true },
                { name: t('menu:monitoring.plsqlEditor'), action: 'plsql-editor', onClick: () => openOwnConnWindow('plsql-editor'), icon: <FiCode />, opensWindow: true },
                MENU_SEPARATOR,
                // 복사·붙여넣기·실행 취소처럼 어느 앱에나 있는 조작은 메뉴에 두지 않는다 — 우클릭·단축키로 충분하고,
                // 툴바에 둘지는 설정 › 도구 모음에서 고른다 (docs/ui-command-placement.md 규칙 3)
                { name: t('menu:edit.find'), action: 'find', onClick: onEditorFind, shortcut: 'Ctrl+F', icon: <FiSearch /> },
                { name: t('menu:edit.replace'), action: 'replace', onClick: onEditorReplace, shortcut: 'Ctrl+H', icon: <FiRefreshCw /> },
                { name: t('menu:edit.globalFind'), action: 'global-find', onClick: onGlobalSearch, shortcut: 'Ctrl+Shift+F', icon: <FiSearch /> },
                MENU_SEPARATOR,
                // 이 앱의 SQL 편집 기능 — 메뉴에서 못 보면 있는 줄 모른다 (규칙 2). action id 는 툴바 레지스트리와 같다
                { name: t('menu:edit.format'), action: 'format', onClick: onEditorFormat, icon: <FiAlignLeft /> },
                { name: t('menu:edit.toggleComment'), action: 'toggleComment', onClick: onEditorToggleComment, shortcut: 'Ctrl+/', icon: <FiHash /> },
                { name: t('menu:edit.uppercase'), action: 'uppercase', onClick: onEditorUppercase, icon: <FiType /> },
                { name: t('menu:edit.lowercase'), action: 'lowercase', onClick: onEditorLowercase, icon: <FiType /> },
            ],
        },
        {
            id: 'view',
            name: t('menu:view.title'),
            icon: <FiEye />,
            // 켜고 끄는 항목 — checked 가 있으면 ✓ 로 상태를 보이고 즐겨찾기 아이콘을 두지 않는다
            // (docs/ui-command-placement.md §2). 상태와 함수는 화면 컨트롤과 같은 것을 쓴다.
            subItems: [
                { name: t('menu:view.columnSelect'), action: 'view-column-select', checked: !!viewState.columnSelection, onClick: () => onToggleView && onToggleView('columnSelection') },
                { name: t('menu:view.showComments'), action: 'view-comments', checked: !!viewState.showComments, onClick: () => onToggleView && onToggleView('showComments') },
                MENU_SEPARATOR,
                { name: t('menu:view.split'), action: 'view-split', checked: !!viewState.split, onClick: () => onToggleView && onToggleView('split') },
                { name: t('menu:view.detach'), action: 'view-detach', checked: !!viewState.detach, onClick: () => onToggleView && onToggleView('detach') },
                MENU_SEPARATOR,
                { name: t('menu:view.assistant'), action: 'view-assistant', favorite: true, checked: !!viewState.assistant, onClick: () => onToggleView && onToggleView('assistant') },
                MENU_SEPARATOR,
                { name: t('menu:view.dark'), action: 'view-dark', checked: !!darkMode, onClick: onToggleDarkMode },
            ],
        },
        {
            id: 'run',
            name: t('menu:run.title'),
            icon: <FiPlay />,
            subItems: [
                { name: t('menu:run.current'), action: 'executecurrent', onClick: onExecuteCurrent, shortcut: 'Ctrl+⏎', icon: <FiPlay /> },
                { name: t('menu:run.all'), action: 'executeall', onClick: onExecuteAll, shortcut: 'F5', icon: <FiPlayCircle /> },
                { name: t('menu:run.history'), action: 'viewhistory', onClick: onViewExecutionHistory, icon: <FiClock /> },
                { name: t('menu:run.plan'), action: 'runplan', onClick: onExecutePlan, shortcut: 'Ctrl+E', icon: <FiBarChart2 /> },
                { name: t('menu:run.schemaInfo'), action: 'viewschema', onClick: onViewSchemaInfo, shortcut: 'F4', icon: <FiDatabase /> },
                MENU_SEPARATOR,
                { name: t('menu:run.commit'), action: 'commit', onClick: onCommit, icon: <FiCheckCircle /> },
                { name: t('menu:run.rollback'), action: 'rollback', onClick: onRollback, icon: <FiXCircle /> },
                // { name: '선택 실행 (F9)', action: 'runselected' },
                // { name: '현재 줄 실행 (Ctrl+Shift+Enter)', action: 'runcurrentline' },
            ],
        },
        {
            id: 'monitoring',
            // 창을 여는 메뉴 — 즐겨찾기하면 툴바 오른편 도구 창 묶음에 **아이콘+레이블**로 붙는다(2026-09-13)
            opensWindow: true,
            name: t('menu:monitoring.title'),
            icon: <FiActivity />,
            subItems: [
                // 실시간 대시보드 · 수집 설정 · 쿼리 템플릿 순, 구분선 없이(사용자 요청 2026-10-18). 쿼리 템플릿은 관리자만 여는
                // 전역 설정이라 관리자가 아니면 감춘다
                { name: t('menu:monitoring.dashboard'), action: 'monitoring-dashboard', onClick: () => setShowMonitoringDashboard(true), icon: <FiActivity /> },
                { name: t('menu:monitoring.collectSettings'), action: 'monitoring-settings', onClick: () => setShowMonitoringCriteria(true), icon: <FiSettings /> },
                ...(monAccess.admin ? [
                    { name: t('menu:monitoring.queryTemplates'), action: 'monitoring-query-templates', onClick: () => setShowQueryTemplateManager(true), icon: <FiSettings /> },
                ] : []),
            ],
        },
        // **도구 메뉴는 AI 제외 빌드에도 있다**(2026-09-20). SQL 실행계획 분석은 AI 없이 도는 기능이라
        // 메뉴째 없애면 그 빌드에서는 여는 길이 툴바 버튼뿐이 된다. AI 가 필요한 두 창
        // (튜닝 사례·테이블 분석)만 조건부로 둔다.
        {
            id: 'tools',
            name: t('menu:tools.title'),
            icon: <FiTool />,
            // 2026-10-04 — 최상위는 「DB 전문가」, 그 아래 DBMS 별 묶음(펼침)과 DBMS 공통 튜닝 도구.
            // 창을 여는 기능은 반드시 메뉴에 둔다(규칙 1). action id 는 그대로다 — 즐겨찾기·툴바 버튼이 action 으로 묶여 있어
            // 묶음 안으로 옮겨도 남는다(액션 실행·게시는 flatSubItems 로 펼쳐서 훑는다). 창을 여는 항목은 opensWindow.
            subItems: [
                {
                    name: t('menu:tools.oracleExpert'), action: 'group-oracle-expert', icon: <FiDatabase />,
                    children: [
                        { name: t('menu:monitoring.oraclePerf'), action: 'monitoring-oracle-perf', onClick: () => openOwnConnWindow('oracle-perf'), icon: <FiBarChart2 />, opensWindow: true },
                        { name: t('menu:monitoring.sessionAdmin'), action: 'session-admin', onClick: () => openOwnConnWindow('session-admin'), icon: <FiUserX />, opensWindow: true },
                        // 락 대기 — 오라클 성능 분석의 하위 탭이던 것을 별도 창으로(2026-10-07)
                        { name: t('menu:monitoring.lockWait'), action: 'lock-wait', onClick: () => openOwnConnWindow('lock-wait'), icon: <FiLock />, opensWindow: true },
                        { name: t('menu:monitoring.resourceAnalysis'), action: 'resource-analysis', onClick: () => openOwnConnWindow('resource-analysis'), icon: <FiHardDrive />, opensWindow: true },
                        { name: t('menu:monitoring.dbmsCatalog'), action: 'dynamic-view-explorer', onClick: () => openOwnConnWindow('dbms-catalog'), icon: <FiDatabase />, opensWindow: true },
                        // 테이블 분석은 Oracle 전용(사용자 결정 2026-09-11)이라 여기
                        ...(AI_ENABLED ? [
                            MENU_SEPARATOR,
                            // 설계 두 갈래(2026-10-15 개편) — 각 창이 「테이블 액세스 분석」 탭을 함께 갖는다(분석 메뉴는 따로 두지 않는다).
                            // 같은 창 하나가 모드로 열리므로 수집한 분석은 두 설계가 함께 쓰고, 저장한 분석도 어느 쪽에서든 불러온다
                            { name: t('menu:tools.indexDesign'), action: 'tool-index-design', onClick: () => onOpenTableAnalysis('index'), icon: <FiDatabase /> },
                            { name: t('menu:tools.partitionDesign'), action: 'tool-partition-design', onClick: () => onOpenTableAnalysis('partition'), icon: <FiDatabase /> },
                            { name: t('menu:tools.designGuides'), action: 'tool-design-guides', onClick: onOpenDesignGuides, icon: <FiBookOpen /> },
                        ] : []),
                        // PL/SQL 편집기는 「작성」 메뉴로 옮겼다(2026-10-19)
                    ],
                },
                {
                    // 개발 중 — 계획: docs/postgresql-expert-plan.md. 지금 PG 에서 되는 것은 DBMS 카탈로그뿐이다
                    name: t('menu:tools.pgExpert'), action: 'group-pg-expert', icon: <FiDatabase />, badge: t('menu:tools.inDevelopment'),
                    children: [
                        { name: t('menu:monitoring.dbmsCatalog'), action: 'pg-dynamic-view-explorer', onClick: () => openOwnConnWindow('dbms-catalog'), icon: <FiDatabase /> },
                        { name: t('menu:tools.pgPlanned'), action: 'pg-planned', disabled: true },
                    ],
                },
                MENU_SEPARATOR,
                // DBMS 공통 — 실행계획 분석·보관함·튜닝 사례는 연결 종류와 무관하다
                // SQL 실행계획 분석은 편집기 실행계획(Ctrl+E · 실행계획 탭의 옵션)에 합쳤다(2026-10-26 사용자 결정)
                // SQL 튜닝 — 대상 등록부터 리포트까지 한 작업(AI 무관하게 열린다, docs/features/20-sql-tuning.md)
                { name: t('menu:tools.sqlTuning'), action: 'tool-sql-tuning', onClick: onOpenSqlTuning, icon: <FiActivity />, opensWindow: true },
                // SQL 진단 리포트 — DB 연결 없이 쿼리·실행계획 글만으로(AI 무관하게 열린다 — 입력·기계 분석·리포트, docs/features/22-sql-review.md)
                { name: t('menu:tools.sqlReview'), action: 'tool-sql-review', onClick: onOpenSqlReview, icon: <FiFileText />, opensWindow: true },
                // SQL 작성·SQL 빌더는 「작성」 메뉴로 옮겼다(2026-10-19)
                // SQL 수집(2026-10-27) — 수집 → 클린징 → 변조 → 검증 → 저장·머지. 세트를 품질 검사 등이 대상으로 쓴다(docs/features/29-sql-collect.md)
                { name: t('menu:tools.sqlCollect'), action: 'tool-sql-collect', onClick: onOpenSqlCollect, icon: <FiDatabase />, opensWindow: true },
                // SQL 품질 검사(2026-10-19) — 기준 → 대상(공유 풀·AWR·CSV) → 기계 검사 → 결과·검토 → 리포트. AI 무관하게 열린다(AI 판정만 AI_ENABLED, docs/features/27-sql-quality.md)
                { name: t('menu:tools.sqlQuality'), action: 'tool-sql-quality', onClick: onOpenSqlQuality, icon: <FiCheckSquare />, opensWindow: true },
                // 스키마 비교(2026-10-26) — 비교 대상 → 오브젝트 → 결과(종류별) → 동기화 스크립트. AI 무관 · DB 를 바꾸지 않는다(docs/features/28-schema-compare.md)
                { name: t('menu:tools.schemaCompare'), action: 'tool-schema-compare', onClick: onOpenSchemaCompare, icon: <FiColumns />, opensWindow: true },
                // 보관함은 **AI 제외 빌드에도 있다** — 저장은 AI 와 무관한 기능이다
                { name: t('menu:tools.planArchive'), action: 'tool-plan-archive', onClick: onOpenPlanArchive, icon: <FiBookmark /> },
                ...(AI_ENABLED ? [
                    { name: t('menu:tools.tuningCases'), action: 'tool-tuning-cases', onClick: onOpenCaseList, icon: <FiBookOpen /> },
                ] : []),
            ],
        },
        {
            id: 'challenge',
            name: t('menu:challenge.title'),
            icon: <FiAward />,
            // 스탠드얼론에서도 연다 (2026-09-19) — 파일로 가져온 챌린지를 로컬에서 풀 수 있다.
            // 막아 두면 가져오기 버튼이 있는 화면에 닿을 방법이 없다.
            subItems: [
                { name: t('menu:challenge.query'), action: 'challengejoin', onClick: onChallengeJoin, icon: <FiAward /> },
                // 튜닝 문제(003 SQL 개선 · 004 인덱스 설계)는 쿼리챌린지와 **같은 챌린지 데이터**에 산다(2026-09-19).
                // 이 메뉴는 같은 창을 튜닝 문항이 든 챌린지만 걸러 연다 — 따로 저장하는 데이터는 없다(2026-10-04)
                { name: t('menu:challenge.tuning'), action: 'tuningchallenge', onClick: onChallengeTuning, icon: <FiZap /> },
            ],
        },
        // ... (다른 메뉴 항목들) ...
        {
            id: 'help',
            name: t('menu:help.title'),
            icon: <FiInfo />,
            // 도움말 항목은 즐겨찾기를 두지 않는다 — 툴바에 버튼으로 붙을 기능이 아니다(사용자 요청 2026-09-13).
            // 왼쪽 아이콘은 표시만 하고, 툴바 버스에도 싣지 않아 옛 저장본에 남은 id 도 버튼이 되지 않는다.
            noFavorite: true,
            // 관리자에게만 숫자가 붙는다 — 로그 창을 열면 사라진다
            badge: (logAlerts.total + feedbackReplies) ? String(logAlerts.total + feedbackReplies) : null,
            badgeAlert: true,
            subItems: [
                { name: t('menu:help.guide'), action: 'help-guide', onClick: () => setShowHelpGuide(true), icon: <FiBookOpen /> },
                ...(onAskAssistant
                    ? [{ name: t('menu:help.askAgent'), action: 'help-ask', onClick: () => onAskAssistant(t('assistant:help.btn.quickStart')), icon: <FiMessageSquare /> }]
                    : []),
                // AI 가드레일 — AI 에게 보내지 않을 것·AI 가 혼자 하지 않는 것(docs/features/21-ai-guardrail.md).
                // AI 제외 빌드에도 둔다 — 내보내기 검사가 같은 정책을 쓴다
                { name: t('menu:help.guardrail'), action: 'help-guardrail', onClick: openGuardrail, icon: <FiShield /> },
                // 진화 기록 — 쓰면서 배운 것을 보고 고친다(명세 24). LLM 없이 도는 화면이라 AI 제외 빌드에도 둔다
                {
                    name: t('menu:help.experience'), action: 'help-experience', onClick: onOpenExperience, icon: <FiTrendingUp />,
                    badge: evoNotices || null, badgeTitle: t('menu:help.experienceNotices', { count: evoNotices }),
                },
                // 사용·에러 로그 — 도구 › 에서 옮겼다(2026-09-13). 무엇이 났는지 사용자가 직접 보는 창
                {
                    name: t('menu:help.opsLog'), action: 'help-ops-log', onClick: onOpenOpsLog, icon: <FiActivity />,
                    badge: logAlerts.total || null,
                    badgeTitle: t('menu:help.alertsTitle', { newGroups: logAlerts.newGroups, regressed: logAlerts.regressed, feedback: logAlerts.feedback }),
                },
                // 문제 신고 — 무엇을 하다 무엇이 났는지 사용자가 직접 알린다(화면 오류 수집 2단계, 2026-09-14).
                // 스탠드얼론에는 두지 않는다: 신고가 그 PC 에만 쌓여 개발자에게 닿지 않는다
                ...(isStandalone
                    ? []
                    : [{
                        name: t('menu:help.report'), action: 'help-report',
                        // 새 답이 있으면 "내 신고" 로 바로 연다
                        onClick: () => openFeedback(feedbackReplies ? { tab: 'mine' } : {}), icon: <FiFlag />,
                        badge: feedbackReplies || null,
                        badgeTitle: feedbackReplies ? t('menu:help.reportReplies', { n: feedbackReplies }) : undefined,
                    }]),
                ...(isStandalone
                    ? [
                        { name: t('menu:help.viewLogs'), action: 'help-logs', onClick: () => setShowLogViewer(true), icon: <FiFileText /> },
                        // 진단 파일 — 오류 묶음·기록·사용 요약. SQL·바인드·메시지는 서버가 뺀다
                        { name: t('menu:help.exportDiagnostics'), action: 'help-diag-export', onClick: handleExportDiagnostics, icon: <FiDownload /> },
                        {
                            name: t('menu:help.checkUpdate'), action: 'help-update', onClick: handleCheckUpdate, icon: <FiRefreshCw />,
                            badge: updateInfo && updateInfo.newer ? `v${updateInfo.latest}` : null,
                            badgeTitle: updateInfo && updateInfo.newer ? t('menu:help.updateAvailable', { version: updateInfo.latest }) : undefined,
                        },
                    ]
                    : []),
                { name: t('menu:help.about'), action: 'about', onClick: () => setShowAboutModal(true), icon: <FiInfo /> },
            ],
        },
    ];

    /**
     * 메뉴 클릭은 **여기 한 곳**을 지난다 — 그래서 기록도 여기서 한다.
     *
     * 항목마다 `logAction` 을 넣으면 메뉴가 늘 때마다 빠뜨린다. 메뉴 항목은 이미 `action` id 를
     * 갖고 있으므로 그것을 로그 id 로 옮기기만 하면 되고, 여기 없는 항목은 **기록되지 않을 뿐**
     * 아무것도 깨지지 않는다(모르는 id 는 서버가 버린다).
     */
    /** 묶음(children)을 펼친 항목 목록 — 액션 실행·즐겨찾기 게시가 이것을 훑는다(묶음 자체는 액션이 아니다) */
    const flatSubItems = (menu) => (menu.subItems || []).flatMap(it => (Array.isArray(it.children) ? it.children : [it]));
    const workspace = useWorkspaceDock();
    // 다른 창(세션 분석)이 「실행 이력」 으로 보내면 — 오라클 성능 분석을 그 연결로 열고 앞으로(2026-10-22). 탭·본문은 창이 고른다(utils/perfJump)
    const perfJump = usePerfJump();
    const perfJumpSeq = perfJump ? perfJump.seq : 0;
    useEffect(() => {
        if (!perfJump) return;
        if (!showOraclePerfAnalysis) setWindowConn(m => ({ ...m, 'oracle-perf': perfJump.connId }));
        setShowOraclePerfAnalysis(true);
        if (workspace) workspace.activateByTitle(t('menu:monitoring.oraclePerf'));
    }, [perfJumpSeq]); // eslint-disable-line react-hooks/exhaustive-deps -- 새 요청에만
    const handleMenuClick = (menuName, subItem) => {
        if (subItem && (Array.isArray(subItem.children) || subItem.disabled)) return;
        if (subItem && subItem.action) logMenuAction(subItem.action);
        if (subItem && subItem.onClick) {
            subItem.onClick();
            // 이미 작업 탭으로 열려 있는 화면이면 그 탭을 앞으로(열려 있으면 open 이 안 바뀌어 창이 스스로 알 수 없다 — WorkspaceDock)
            if (workspace && typeof subItem.name === 'string') workspace.activateByTitle(subItem.name);
        } else {
            console.log(`Action: ${subItem.action}`);
        }
        setActiveMenu(null);
    };

    // 메뉴 즐겨찾기 — 화면 버튼이 없는 메뉴를 켜면 편집기 툴바(App)에 버튼이 붙는다.
    // 핸들러가 이 컴포넌트 안(모달 열기 상태)에 있어 App 이 직접 못 부르므로, 메뉴 목록을
    // 버스에 게시하고 실행은 handleMenuClick 한 경로로 되돌린다 — 메뉴 정의·사용로그 모두 한 곳에 남는다.
    // 모니터링(상시 수집) 메뉴는 'monitoring' 권한이 있을 때만 — 없으면 열어도 403 이다(2026-10-03).
    // 권한을 모르는 동안(응답 전)도 감춘다: 잠깐 보였다 사라지는 것보다 늦게 나타나는 편이 낫다.
    // 사용자 유형이 beginner 면 모니터링 › · DB 전문가 › 를 통째로 뺀다(즐겨찾기 단추도 이 목록에서 나오므로 함께 빠진다)
    const menuItems = allMenuItems.filter(m => (m.id !== 'monitoring' || (canMonitor && expertMenus)) && (m.id !== 'tools' || expertMenus));
    const latestMenuRef = useRef(menuItems);
    latestMenuRef.current = menuItems;
    const handleMenuClickRef = useRef(handleMenuClick);
    handleMenuClickRef.current = handleMenuClick;
    useEffect(() => {
        registerMenuRunner((actionId) => {
            for (const menu of latestMenuRef.current) {
                if (menu.disabled) continue;
                const sub = flatSubItems(menu).find(item => item.action === actionId && !item.disabled);
                if (sub) { handleMenuClickRef.current(menu.id, sub); return; }
            }
        });
        return () => registerMenuRunner(null);
    }, []);
    // 표시명(언어)이나 항목 구성이 바뀔 때만 게시한다 — 매 렌더 게시하면 구독자인 App 이 계속 다시 그린다.
    const publishedSigRef = useRef('');
    useEffect(() => {
        const next = {};
        for (const menu of menuItems) {
            // 즐겨찾기를 두지 않는 메뉴(도움말)는 싣지 않는다 — 실으면 옛 저장본의 id 가 툴바 버튼으로 살아난다
            if (menu.disabled || menu.noFavorite) continue;
            for (const sub of flatSubItems(menu)) {
                if (sub.action === 'separator' || sub.disabled || !sub.onClick || !sub.icon || typeof sub.checked === 'boolean') continue;
                // window — 창을 여는 항목. App 이 이것을 오른편에 아이콘+레이블로 그린다
                next[sub.action] = { name: sub.name, icon: sub.icon, window: !!(menu.opensWindow || sub.opensWindow) };
            }
        }
        const sig = Object.keys(next).map(k => `${k}=${next[k].name}`).join('|');
        if (sig === publishedSigRef.current) return;
        publishedSigRef.current = sig;
        publishMenuActions(next);
    });

    // 메뉴 항목 하나 — 묶음(children)이면 **오른쪽에 붙어 열리는 하위 메뉴**(플라이아웃)다.
    // 이력: 처음 플라이아웃 → 아래로 펼치는 아코디언(사용자 요청) → 「독특하다」 는 지적으로 일반 데스크톱 메뉴의
    // 플라이아웃으로 되돌렸다(사용자 결정 2026-10-06). 아코디언이 싫었던 이유(지나가기만 해도 아래 항목이 밀려 내려간다)는
    // 플라이아웃에는 없다 — 옆에 열리므로 목록이 움직이지 않는다. 다만 지나가기만 해도 번쩍 열리지 않게 **잠깐 머물러야** 열고,
    // 하위 메뉴 쪽으로 비스듬히 옮기는 동안 닫히지 않게 **닫힘을 조금 늦춘다**(일반 메뉴의 hover intent).
    const [openGroup, setOpenGroup] = useState(null);
    const [flyoutPos, setFlyoutPos] = useState({ flip: false, shiftY: 0 });
    const hoverTimerRef = useRef(null);
    const flyoutRef = useRef(null);
    useEffect(() => { setOpenGroup(null); }, [activeMenu]);   // 메뉴를 다시 열면 묶음은 닫힌 채로
    useEffect(() => () => clearTimeout(hoverTimerRef.current), []);
    const scheduleGroup = (action, delay) => {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = setTimeout(() => setOpenGroup(action), delay);
    };
    // 화면 밖으로 나가면 왼쪽으로 뒤집고, 아래로 넘치면 위로 올린다 — 열린 뒤 한 번 재서 정한다
    useLayoutEffect(() => {
        if (!openGroup || !flyoutRef.current) { setFlyoutPos({ flip: false, shiftY: 0 }); return; }
        const rect = flyoutRef.current.getBoundingClientRect();
        const flip = rect.right > window.innerWidth - 4;
        // 화면 px 로 잰 넘침을 CSS px 로 옮긴다(웹의 화면 확대, utils/appZoom)
        const shiftY = rect.bottom > window.innerHeight - 4 ? toCssPx(Math.min(0, window.innerHeight - 4 - rect.bottom)) : 0;
        setFlyoutPos({ flip, shiftY });
    }, [openGroup]);
    /** 하위 메뉴 안에서 ↑↓ 로 옮긴다 — 항목은 tabIndex=-1 이라 포커스만 받는다 */
    const moveFocus = (e, dir) => {
        const items = [...e.currentTarget.parentElement.querySelectorAll(':scope > li[tabindex]')];
        const i = items.indexOf(e.currentTarget);
        const next = items[(i + dir + items.length) % items.length];
        if (next) { e.preventDefault(); next.focus(); }
    };
    const renderSubItem = (menu, subItem, index, inFlyout = false) => {
        if (Array.isArray(subItem.children)) {
            const open = openGroup === subItem.action;
            const openAndFocus = () => {
                clearTimeout(hoverTimerRef.current);
                setOpenGroup(subItem.action);
                // 그려진 뒤 첫 항목으로
                setTimeout(() => {
                    const first = flyoutRef.current && flyoutRef.current.querySelector(':scope > li[tabindex]');
                    if (first) first.focus();
                }, 0);
            };
            return (
                <li
                    key={subItem.action}
                    className={`${styles.dropdownItem} ${styles.dropdownGroup}${open ? ` ${styles.dropdownGroupOpen}` : ''}`}
                    role="menuitem" aria-haspopup="true" aria-expanded={open} tabIndex={-1}
                    onMouseEnter={() => scheduleGroup(subItem.action, open ? 0 : 150)}
                    onMouseLeave={() => scheduleGroup(null, 300)}
                    // 누르면 바로 연다(터치에는 마우스 올림이 없다). 닫지는 않는다 — 열린 묶음을 다시 누르는 것은 대개 그 안을 고르려는 것이다
                    onClick={(e) => { e.stopPropagation(); clearTimeout(hoverTimerRef.current); setOpenGroup(subItem.action); }}
                    onKeyDown={(e) => {
                        if (e.target !== e.currentTarget) return;
                        if (e.key === 'ArrowRight' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openAndFocus(); }
                        else if (e.key === 'ArrowDown') moveFocus(e, 1);
                        else if (e.key === 'ArrowUp') moveFocus(e, -1);
                    }}
                >
                    <span className={styles.menuItemContent}>
                        {subItem.icon && <span className={styles.subMenuIcon}>{subItem.icon}</span>}
                        <span className={styles.menuItemName}>{subItem.name}</span>
                        {subItem.badge ? <span className={styles.menuItemBadge}>{subItem.badge}</span> : null}
                    </span>
                    <FiChevronRight className={styles.dropdownGroupArrow} />
                    {open && (
                        <ul
                            ref={flyoutRef}
                            role="menu"
                            className={`${styles.dropdownMenu} ${styles.dropdownFlyout}${flyoutPos.flip ? ` ${styles.dropdownFlyoutFlip}` : ''}`}
                            style={flyoutPos.shiftY ? { marginTop: flyoutPos.shiftY } : undefined}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                                // ← · Esc 는 하위 메뉴만 닫고 묶음으로 돌아간다
                                if (e.key === 'ArrowLeft' || e.key === 'Escape') {
                                    e.preventDefault(); e.stopPropagation();
                                    setOpenGroup(null);
                                    const groupLi = e.currentTarget.parentElement;
                                    if (groupLi) groupLi.focus();
                                }
                            }}
                        >
                            {subItem.children.map((child, ci) => renderSubItem(menu, child, ci, true))}
                        </ul>
                    )}
                </li>
            );
        }
        if (subItem.disabled) {
            return (
                <li key={subItem.action || `disabled-${index}`} className={`${styles.dropdownItem} ${styles.dropdownItemDisabled}`} aria-disabled="true">
                    <span className={styles.menuItemContent}><span className={styles.subMenuIcon} /><span className={styles.menuItemName}>{subItem.name}</span></span>
                </li>
            );
        }
                                        if (subItem.action === 'separator') {
            return <li key={`separator-${index}`} className={styles.menuSeparator}></li>;
        }
        // 즐겨찾기 — 왼쪽 아이콘이 토글이다. 화면에 원래 버튼이 있는 메뉴는 그 버튼을 보이기/숨기기,
        // 없는 메뉴는 편집기 툴바에 붙이기/떼기 (utils/actionFavorites.js).
        // 표시명은 번역되므로 로직 비교에 쓰면 안 된다 — 안정적인 id 로 판별한다
        // 켜고 끄는 항목(보기 메뉴) — ✓ 가 왼쪽 자리를 쓰므로 즐겨찾기를 두지 않는다
        const isCheckable = typeof subItem.checked === 'boolean';
        const pinnable = !isCheckable && !menu.noFavorite && !!onToggleFavorite && !!subItem.icon
            && (!!subItem.onClick || appActionKeys.includes(subItem.action));
        const isPinned = pinnable && !!isFavoriteAction && isFavoriteAction(subItem.action);
        // 켜고 끄는 항목 중 단추 줄에 단추가 있는 것(분석 모드·AI 에이전트) — 즐겨찾기를 오른쪽 끝의 별로 둔다(2026-10-19)
        const checkPinnable = isCheckable && !!subItem.favorite && !!onToggleFavorite;
        const checkPinned = checkPinnable && !!isFavoriteAction && isFavoriteAction(subItem.action);
        return (
            <li
                key={subItem.name}
                onClick={() => handleMenuClick(menu.id, subItem)}
                className={styles.dropdownItem}
                tabIndex={-1}
                // 바깥 목록의 다른 항목에 올리면 열린 하위 메뉴를 닫는다(조금 늦게 — 비스듬히 지나가는 길일 수 있다)
                onMouseEnter={inFlyout ? undefined : () => { if (openGroup) scheduleGroup(null, 300); }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleMenuClick(menu.id, subItem); }
                    else if (e.key === 'ArrowDown') moveFocus(e, 1);
                    else if (e.key === 'ArrowUp') moveFocus(e, -1);
                }}
                {...(isCheckable ? { role: 'menuitemcheckbox', 'aria-checked': subItem.checked } : { role: 'menuitem' })}
            >
                <span className={styles.menuItemContent}>
                    {isCheckable && (
                        <span className={`${styles.subMenuIcon} ${styles.menuCheck}`} aria-hidden="true">
                            {subItem.checked ? <FiCheck /> : null}
                        </span>
                    )}
                    {!isCheckable && subItem.icon && (
                        pinnable ? (
                            <button
                                type="button"
                                className={`${styles.subMenuIcon} ${styles.subMenuIconFav}${isPinned ? ` ${styles.subMenuIconFavOn}` : ''}`}
                                // 누르는 순간의 기본 동작을 막는다 — span 이던 때는 글자를 누른 것으로 처리돼
                                // 편집기 포커스가 빠지고 레이블 앞에 텍스트 커서가 깜빡였다(사용자 지적 2026-09-13).
                                // 즐겨찾기만 바꾸는 것이니 포커스는 원래 자리(편집기)에 둔다.
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    logAction('pref.favorite');
                                    onToggleFavorite(subItem.action);
                                }}
                                title={isPinned ? t('menu:favorite.remove') : t('menu:favorite.add')}
                                aria-label={isPinned ? t('menu:favorite.remove') : t('menu:favorite.add')}
                                aria-pressed={isPinned}
                            >
                                {subItem.icon}
                            </button>
                        ) : (
                            <span className={styles.subMenuIcon}>{subItem.icon}</span>
                        )
                    )}
                    <span className={styles.menuItemName}>{subItem.name}</span>
                    {subItem.badge ? <span className={styles.menuItemBadge} title={subItem.badgeTitle}>{subItem.badge}</span> : null}
                </span>
                {subItem.shortcut && <span className={styles.menuItemShortcut}>{subItem.shortcut}</span>}
                {checkPinnable && (
                    <button
                        type="button"
                        className={`${styles.menuItemPin}${checkPinned ? ` ${styles.menuItemPinOn}` : ''}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={(e) => { e.stopPropagation(); logAction('pref.favorite'); onToggleFavorite(subItem.action); }}
                        title={checkPinned ? t('menu:favorite.remove') : t('menu:favorite.add')}
                        aria-label={checkPinned ? t('menu:favorite.remove') : t('menu:favorite.add')}
                        aria-pressed={checkPinned}
                    >
                        <FiStar size={12} />
                    </button>
                )}
            </li>
        );
    };

    const toggleSubMenu = (menuName) => {
        setActiveMenu(activeMenu === menuName ? null : menuName);
    };

    const confirm = useConfirm();

    const handleLogout = async () => {
        // 로그아웃은 이 브라우저의 사용자별 저장물(열린 탭·프로젝트 자동저장분)을 지운다 — 파일로 저장하지 않은
        // 탭이 있으면 그 사실을 먼저 말한다. 예전에는 "로그아웃하시겠습니까?" 뿐이라 작업이 조용히 사라졌다(2026-10-03)
        const ok = await confirm({
            title: t('menu:user.logout'),
            message: unsavedTabCount > 0
                ? t('menu:user.logoutUnsaved', { count: unsavedTabCount })
                : t('menu:user.logoutConfirm'),
            confirmText: t('menu:user.logout'),
            cancelText: t('common:action.cancel'),
            variant: 'danger'
        });
        if (ok) {
            dispatch(logout());
        }
        setUserMenuOpen(false);
    };
    
    // 체험 계정(아이디가 edu 로 시작 — 로그인 화면의 체험 단추로 여럿이 함께 쓴다)은 닉네임·비밀번호 변경과 탈퇴를 감춘다(2026-10-15 요청).
    // 한 사람이 비밀번호를 바꾸거나 탈퇴하면 다른 사람이 못 들어온다. 막는 것은 **화면만**이다(사용자 결정 — 서버는 그대로).
    const isTrialAccount = /^edu/i.test(String(user?.loginId || user?.username || ''));

    const handleNicknameChange = () => {
        setShowNicknameChangeModal(true);
        setUserMenuOpen(false);
    };
    
    const handlePasswordChange = () => {
        setShowPasswordChangeModal(true);
        setUserMenuOpen(false);
    };

    const handleWithdrawal = () => {
        setShowWithdrawalModal(true);
        setUserMenuOpen(false);
    };

    /**
     * 참여그룹(2026-10-07) — **가입은 서버**(QBase joinChallengeGroup · leaveChallengeGroup · myChallengeGroups). 그 그룹에 공개된
     * 챌린지는 서버가 구성원에게만 보여 준다. 예전에는 전체 그룹 목록을 초대코드째 받아 브라우저가 대조하고 가입을 브라우저에만
     * 남겼다. 「지금 쓰는 그룹」(제출 기록에 실리는 참여그룹)은 여전히 이 브라우저가 기억하지만 **가입한 그룹 중에서만** 고른다.
     */
    const announceGroup = (group) => {
        if (group) localStorage.setItem('selectedParticipationGroup', JSON.stringify(group));
        else localStorage.removeItem('selectedParticipationGroup');
        window.dispatchEvent(new CustomEvent('participationGroupChanged', { detail: { selectedGroup: group } }));
    };

    const refreshMyGroups = async () => {
        try {
            const list = await listMyChallengeGroups();
            setMyGroups(list);
            // 서버에서 빠진 그룹(탈퇴·그룹 중지)을 계속 「지금 쓰는 그룹」 으로 두지 않는다
            setSelectedGroup((cur) => {
                if (cur && !list.some(g => g.qryChallengePtcpGrpId === cur.qryChallengePtcpGrpId)) { announceGroup(null); return null; }
                return cur;
            });
            return list;
        } catch (_e) {
            setMyGroups([]);
            return [];
        }
    };

    const toggleParticipationGroupMenu = () => {
        setParticipationGroupMenuOpen(!participationGroupMenuOpen);
        setUserMenuOpen(false); // 사용자 메뉴가 열려있다면 닫기
        if (!participationGroupMenuOpen) refreshMyGroups();   // 열 때마다 서버 기준으로
    };

    const handleJoinGroup = () => {
        setShowParticipationGroupModal(true);
        setParticipationGroupMenuOpen(false);
    };

    /** 가입한 그룹 중 하나를 「지금 쓰는 그룹」 으로 — 이미 구성원이라 코드를 다시 묻지 않는다 */
    const handleSelectGroup = (group) => {
        setSelectedGroup(group);
        announceGroup(group);
        setParticipationGroupMenuOpen(false);
    };

    /** 「지금 쓰는 그룹」 만 내려놓는다(탈퇴가 아니다) */
    const handleClearGroup = () => {
        setSelectedGroup(null);
        announceGroup(null);
        setParticipationGroupMenuOpen(false);
    };

    /** 탈퇴(서버) — 그 그룹에만 공개된 챌린지가 목록에서 사라진다 */
    const handleLeaveGroup = async () => {
        if (!selectedGroup) return;
        setParticipationGroupMenuOpen(false);
        const ok = await confirm({
            title: t('menu:user.leaveTitle'),
            message: t('menu:user.leaveConfirm', { name: selectedGroup.ptcpGrpNm }),
            confirmText: t('menu:user.leaveGroup'),
            variant: 'danger',
        });
        if (!ok) return;
        try {
            await leaveChallengeGroup(selectedGroup.qryChallengePtcpGrpId);
            toast.success(t('menu:user.leftGroup', { name: selectedGroup.ptcpGrpNm }));
            setSelectedGroup(null);
            announceGroup(null);
            await refreshMyGroups();
            dispatch(loadChallengeList());   // 보이는 챌린지가 바뀌었다(서버가 다시 거른다)
        } catch (e) {
            toast.error(formatApiError(e, t));
        }
    };

    const onJoinGroupSuccess = async (group) => {
        toast.success(t('menu:user.joinedGroup', { name: group.ptcpGrpNm }));
        setSelectedGroup(group);
        announceGroup(group);
        await refreshMyGroups();
        dispatch(loadChallengeList());   // 그 그룹에 공개된 챌린지가 이제 보인다
    };

    const toggleUserMenu = () => {
        setUserMenuOpen(!userMenuOpen);
        setParticipationGroupMenuOpen(false); // 참여그룹 메뉴가 열려있다면 닫기
    };

    useEffect(() => {
        // 컴포넌트 마운트 시 로컬 스토리지에서 선택된 그룹 복원 — 가입 여부는 메뉴를 열 때 서버로 다시 확인한다(refreshMyGroups)
        const savedGroup = localStorage.getItem('selectedParticipationGroup');
        if (savedGroup) {
            try {
                const parsedGroup = JSON.parse(savedGroup);
                setSelectedGroup(parsedGroup);
                console.log('저장된 참여그룹 복원:', parsedGroup);
            } catch (error) {
                console.error('저장된 참여그룹 파싱 오류:', error);
                localStorage.removeItem('selectedParticipationGroup');
            }
        }
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setActiveMenu(null);
            }
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setUserMenuOpen(false);
            }
            if (participationGroupMenuRef.current && !participationGroupMenuRef.current.contains(event.target)) {
                setParticipationGroupMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // 내용(연결·사용자·언어·메뉴)이 바뀌면 폭이 달라진다 — 그릴 때마다 다시 맞춘다(최대 네 번 재는 것이라 가볍다)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useLayoutEffect(() => { fitHeader(); });

    return (
        <header ref={headerRef} data-compact="0" className={isProdConn ? `${styles.appHeader} ${styles.prodHeader}` : styles.appHeader}>
            <div className={styles.headerLeft}>
                <span className={styles.appTitle}><img src={`${process.env.PUBLIC_URL}/${darkMode ? 'logo_title_dark' : 'logo_title'}.png`} alt="LiveSQL" className={styles.appLogoTitle} /> <span className={styles.appVersion}>beta</span></span>
                <nav className={styles.menuBar} ref={menuRef}>
                    {menuItems.map((menu) => (
                        <div key={menu.id} className={styles.menuContainer}>
                            <button
                                className={`${styles.menuItem} ${menu.disabled ? styles.menuItemDisabled : ''}`}
                                onClick={() => !menu.disabled && toggleSubMenu(menu.id)}
                                title={menu.disabled ? menu.tooltip : undefined}
                                aria-label={menu.name}
                                data-menu-name={menu.disabled ? undefined : menu.name}
                                style={menu.disabled ? { opacity: 0.45, cursor: 'not-allowed' } : {}}
                            >
                                {menu.icon && <span className={styles.menuIcon}>{menu.icon}</span>}
                                {/* 좁으면 이름을 숨기고 아이콘만(data-compact ≥ 2) — 이름은 툴팁으로 남는다 */}
                                <span className={styles.menuLabel}>{menu.name}</span>
                                {menu.badge && <sup className={menu.badgeAlert ? `${styles.menuBadge} ${styles.menuBadgeAlert}` : styles.menuBadge}>{menu.badge}</sup>}
                            </button>
                            {!menu.disabled && activeMenu === menu.id && menu.subItems && menu.subItems.length > 0 && (
                                <ul className={styles.dropdownMenu}>
                                    {menu.subItems.map((subItem, index) => renderSubItem(menu, subItem, index))}
                                </ul>
                            )}
                        </div>
                    ))}
                </nav>
            </div>
            {/* 실행 대상(연결 유형·연결·세션)은 편집기 오른쪽 위 SessionPicker 가 말한다(2026-10-14 — 여기 배지는 같은 말을 두 번 했다).
                운영 연결이면 머리줄 아래 빨간 줄(prodHeader)은 그대로 남긴다. 가운데 자리는 머리줄 맞춤(fitHeader)이 쓰므로 비워 둔다 */}
            <div className={styles.headerCenter} />
            <div className={styles.headerRight}>
                {/* 화면 확대/축소 — 앱 전체 한 배율(utils/appZoom). Ctrl+휠·Ctrl+= / - / 0 과 같은 값이다 */}
                <div className={styles.zoomControls} title={t('menu:zoom.hint')}>
                    <button
                        className={styles.zoomButton}
                        onClick={() => onZoomChange(Math.max(50, screenZoom - 10))}
                        title={t('menu:zoom.out')}
                    >
                        −
                    </button>
                    <span className={styles.zoomDisplay}>{screenZoom}%</span>
                    <button
                        className={styles.zoomButton}
                        onClick={() => onZoomChange(Math.min(200, screenZoom + 10))}
                        title={t('menu:zoom.in')}
                    >
                        +
                    </button>
                    <button
                        className={styles.zoomResetButton}
                        onClick={() => onZoomChange(100)}
                        title={t('menu:zoom.reset')}
                    >
                        100%
                    </button>
                </div>

                {/* 언어 전환 — 다크모드 토글과 같은 자리·같은 방식(localStorage 유지) */}
                <select
                    className={styles.languageSelect}
                    value={i18n.language}
                    onChange={(e) => setLanguage(e.target.value)}
                    title={t('common:language.label')}
                    aria-label={t('common:language.label')}
                >
                    {SUPPORTED_LANGUAGES.map(l => (
                        <option key={l.code} value={l.code}>{l.label}</option>
                    ))}
                </select>

                {/* 다크모드 토글 */}
                <button
                    className={styles.darkModeToggle}
                    onClick={onToggleDarkMode}
                    title={darkMode ? t('menu:theme.toLight') : t('menu:theme.toDark')}
                >
                    {darkMode ? '☀️' : '🌙'}
                </button>

                {/* 참여그룹 선택 드롭다운 — 스탠드얼론에서 숨김 */}
                {!isStandalone && <div className={styles.participationGroupInfo} ref={participationGroupMenuRef}>
                    <button
                        className={styles.participationGroupButton}
                        onClick={toggleParticipationGroupMenu}
                        title={t('menu:group.select')}
                    >
                        <span className={styles.groupIcon}><FiUsers /></span>
                        <span className={styles.groupName}>
                            {selectedGroup ? selectedGroup.ptcpGrpNm : t('menu:group.fallback')}
                        </span>
                        <span className={styles.dropdownArrow}>▼</span>
                    </button>

                    {participationGroupMenuOpen && (
                        <div className={styles.participationGroupDropdown}>
                            <div className={styles.participationGroupDropdownHeader}>
                                <div className={styles.participationGroupTitle}>{t('menu:user.pickGroup')}</div>
                            </div>

                            {selectedGroup && (
                                <>
                                    <div className={styles.currentGroupSection}>
                                        <div className={styles.currentGroupLabel}>{t('menu:user.currentGroup')}</div>
                                        <div className={styles.currentGroupName}>{selectedGroup.ptcpGrpNm}</div>
                                    </div>
                                    <div className={styles.participationGroupDivider}></div>
                                </>
                            )}

                            <button
                                className={styles.participationGroupDropdownItem}
                                onClick={handleJoinGroup}
                            >
                                <span className={styles.joinIcon}><FiUserPlus /></span>
                                {t('menu:user.joinNewGroup')}
                            </button>

                            {myGroups === null ? (
                                <div className={styles.availableGroupsLabel}>{t('menu:user.loadingGroups')}</div>
                            ) : null}
                            {myGroups && myGroups.length > 0 && (
                                <>
                                    <div className={styles.participationGroupDivider}></div>
                                    <div className={styles.availableGroupsLabel}>{t('menu:user.myGroups')}</div>
                                    {myGroups.map((group) => (
                                        <button
                                            key={group.qryChallengePtcpGrpId}
                                            className={`${styles.participationGroupDropdownItem} ${
                                                selectedGroup?.qryChallengePtcpGrpId === group.qryChallengePtcpGrpId ? styles.selectedGroup : ''
                                            }`}
                                            onClick={() => handleSelectGroup({ qryChallengePtcpGrpId: group.qryChallengePtcpGrpId, ptcpGrpNm: group.ptcpGrpNm })}
                                        >
                                            <span className={styles.groupSelectIcon}>
                                                {selectedGroup?.qryChallengePtcpGrpId === group.qryChallengePtcpGrpId ? '✓' : <FiUsers />}
                                            </span>
                                            {group.ptcpGrpNm}
                                        </button>
                                    ))}
                                </>
                            )}
                            {myGroups && myGroups.length === 0 && (
                                <div className={styles.availableGroupsLabel}>{t('menu:user.noGroups')}</div>
                            )}

                            {selectedGroup && (
                                <>
                                    <div className={styles.participationGroupDivider}></div>
                                    <button className={styles.participationGroupDropdownItem} onClick={handleClearGroup}>
                                        <span className={styles.groupSelectIcon}><FiUsers /></span>
                                        {t('menu:user.clearGroup')}
                                    </button>
                                    <button className={styles.participationGroupDropdownItem} onClick={handleLeaveGroup}>
                                        <span className={styles.leaveIcon}><FiUserMinus /></span>
                                        {t('menu:user.leaveGroup')}
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>}

                {!isStandalone && <div className={styles.userInfo} ref={userMenuRef}>
                    <button
                        className={styles.userButton}
                        onClick={toggleUserMenu}
                        title={t('menu:user.menu')}
                    >
                        <span className={styles.userIcon}><FiUser /></span>
                        <span className={styles.userName}>{user?.nickname || user?.name || user?.username || t('menu:user.fallbackName')}</span>
                        <span className={styles.dropdownArrow}>▼</span>
                    </button>
                    
                    {userMenuOpen && (
                        <div className={styles.userDropdown}>
                            <div className={styles.userDropdownHeader}>
                                <div className={styles.userDropdownName}>{user?.nickname || user?.name || user?.username}</div>
                                <div className={styles.userDropdownEmail}>{user?.email}</div>
                            </div>
                            <div className={styles.userDropdownDivider}></div>
                            <button
                                className={styles.userDropdownItem}
                                onClick={handleLogout}
                            >
                                <span className={styles.logoutIcon}><FiLogOut /></span>
                                {t('menu:user.signOut')}
                            </button>
                            {!isTrialAccount && (<>
                            <div className={styles.userDropdownDivider}></div>
                            <button
                                className={styles.userDropdownItem}
                                onClick={handleNicknameChange}
                            >
                                <span className={styles.editIcon}><FiEdit /></span>
                                {t('menu:user.changeNickname')}
                            </button>
                            <button
                                className={styles.userDropdownItem}
                                onClick={handlePasswordChange}
                            >
                                <span className={styles.passwordIcon}><FiKey /></span>
                                {t('menu:user.changePassword')}
                            </button>
                            <button
                                className={styles.userDropdownItem}
                                onClick={handleWithdrawal}
                            >
                                <span className={styles.withdrawalIcon}><FiAlertTriangle /></span>
                                {t('menu:user.deleteAccount')}
                            </button>
                            </>)}
                        </div>
                    )}
                </div>}
            </div>

            {/* 닉네임 변경 모달 */}
            <NicknameChangeModal
                isOpen={showNicknameChangeModal}
                onClose={() => setShowNicknameChangeModal(false)}
            />
            
            {/* 패스워드 변경 모달 */}
            <PasswordChangeModal
                isOpen={showPasswordChangeModal}
                onClose={() => setShowPasswordChangeModal(false)}
            />

            {/* 회원 탈퇴 모달 */}
            <WithdrawalModal
                isOpen={showWithdrawalModal}
                onClose={() => setShowWithdrawalModal(false)}
            />

            {/* 참여그룹 가입 모달 */}
            <ParticipationGroupModal
                isOpen={showParticipationGroupModal}
                onClose={() => setShowParticipationGroupModal(false)}
                onJoined={onJoinGroupSuccess}
                joinedIds={(myGroups || []).map(g => g.qryChallengePtcpGrpId)}
            />

            {/* 세션 설정 — 자동커밋/격리수준/client info/ALTER SESSION */}
            <SessionSettingsModal
                isOpen={showSessionSettings}
                onClose={() => setShowSessionSettings(false)}
                connectionId={activeConnectionId}
                connectionName={activeConnectionName}
                sessionId={activeSession?.sessionId || null}
            />

            {/* 진단 로그 보기 (도움말 > 로그 보기) — 스탠드얼론 전용 */}
            <LogViewerModal
                isOpen={showLogViewer}
                onClose={() => setShowLogViewer(false)}
            />

            {/* Oracle 네이티브 유틸리티 (exp/imp/expdp/impdp) */}
            <OracleUtilModal
                isOpen={showOracleUtil}
                onClose={() => setShowOracleUtil(false)}
                connectionId={activeConnectionId}
                connectionName={activeConnectionName}
            />

            {/* 모니터링 - 쿼리 템플릿 관리 모달 (최상위 메뉴) */}
            <QueryTemplateManagerModal
                isOpen={showQueryTemplateManager}
                onClose={() => setShowQueryTemplateManager(false)}
                onRegisterAsCriterion={(tpl) => {
                    setCriteriaSeed({ nonce: Date.now(), name: tpl.name, description: tpl.description || '', sql: tpl.sql || '' });
                    setShowMonitoringCriteria(true);
                    if (workspace) setTimeout(() => workspace.activateByTitle(t('menu:monitoring.collectSettings')), 0);
                }}
            />

            {/* 모니터링 - 수집 설정 모달 (쿼리 / 매핑 / 스케줄 3개 탭 통합) */}
            <MonitoringCriteriaModal
                isOpen={showMonitoringCriteria}
                onClose={() => setShowMonitoringCriteria(false)}
                seed={criteriaSeed}
            />

            {/* 모니터링 - 실시간 대시보드 모달 */}
            <MonitoringDashboardModal
                isOpen={showMonitoringDashboard}
                onClose={() => setShowMonitoringDashboard(false)}
                connections={connections}
                /* 기본 탭마다 「같은 내용을 더 자세히」 — 분석 창을 메뉴와 같은 길(연결 고르기 포함)로 연다 */
                onOpenWindow={openOwnConnWindow}
                onOpenCollectionSettings={() => setShowMonitoringCriteria(true)}
            />

            {/* 모니터링 - 오라클 성능 분석 (SQL 단위 3탭 + 인스턴스 단위 2탭 통합) */}
            <OraclePerfAnalysisModal
                isOpen={showOraclePerfAnalysis}
                onClose={() => setShowOraclePerfAnalysis(false)}
                connections={connections}
                defaultConnectionId={windowConn['oracle-perf'] || editorConnId}
                onOpenInEditor={onOpenSqlPerfInEditor}
                darkMode={darkMode}
            />

            {/* 모니터링 - DBMS 카탈로그 모달 (Oracle 딕셔너리+V$/GV$ 또는 PostgreSQL pg_catalog, 즐겨찾기 최대 5개 탭) */}
            <DynamicViewExplorerModal
                isOpen={showDynamicViewExplorer}
                onClose={() => setShowDynamicViewExplorer(false)}
                connections={connections}
                /* 기본은 지금 쓰고 있는 연결이다 — 없으면 모달이 목록 첫 번째(교육DB)를 잡는다 */
                defaultConnectionId={windowConn['dbms-catalog'] || editorConnId}
            />

            {/* 모니터링 - 자원 분석 모달 (오라클 스토리지/메모리 실시간 사용량 + DBA_HIST 이력 추이) */}
            <ResourceAnalysisModal
                isOpen={showResourceAnalysis}
                onClose={() => setShowResourceAnalysis(false)}
                connections={connections}
                darkMode={darkMode}
                defaultConnectionId={windowConn['resource-analysis'] || editorConnId}
            />

            {/* 모니터링 - 락 대기 (차단 세션 트리 + SQL 원문) */}
            <LockWaitModal
                isOpen={showLockWait}
                onClose={() => setShowLockWait(false)}
                connections={connections}
                darkMode={darkMode}
                defaultConnectionId={windowConn['lock-wait'] || editorConnId}
            />

            {/* 모니터링 - PL/SQL 편집기 (Oracle 전용 — 편집/컴파일/DBMS_OUTPUT 캡처 실행) */}
            <PlsqlEditorModal
                isOpen={showPlsqlEditor}
                onClose={() => setShowPlsqlEditor(false)}
                connections={connections}
                defaultConnectionId={windowConn['plsql-editor'] || editorConnId}
            />

            {/* 모니터링 - 세션 관리 (V$SESSION 조회 + KILL SESSION, prod 연결 전용) */}
            <SessionAdminModal
                isOpen={showSessionAdmin}
                onClose={() => setShowSessionAdmin(false)}
                connections={connections}
                darkMode={darkMode}
                defaultConnectionId={windowConn['session-admin'] || editorConnId}
            />

            {/* 독립 연결 창의 연결 고르기 — 그 창의 전담 세션이 없을 때만 뜬다 */}
            <OwnConnChooser
                open={!!chooserWindow}
                title={chooserWindow ? t(chooserWindow.titleKey) : ''}
                icon={chooserWindow ? chooserWindow.icon : null}
                conns={chooserWindow ? connections.filter(chooserWindow.filter) : []}
                defaultConnId={editorConnId}
                onCancel={() => setConnChooser(null)}
                onConfirm={(connId) => {
                    const key = connChooser.key;
                    setConnChooser(null);
                    setWindowConn(m => ({ ...m, [key]: connId }));
                    OWN_CONN_WINDOWS[key].open(true);
                }}
            />

            <HelpGuideModal
                open={showHelpGuide}
                onClose={() => setShowHelpGuide(false)}
                onAsk={onAskAssistant ? (text) => { setShowHelpGuide(false); onAskAssistant(text); } : undefined}
            />

            {/* About 모달 */}
            {showAboutModal && (
                <div className={styles.modalOverlay} onClick={() => setShowAboutModal(false)}>
                    <div className={styles.aboutModal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.aboutModalHeader}>
                            <h4>{t('menu:about.title')}</h4>
                            <button
                                className={styles.closeButton}
                                onClick={() => setShowAboutModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div className={styles.aboutModalContent}>
                            <h2>Live SQL<sup className={styles.aboutBeta}>beta</sup></h2>

                            <div className={styles.aboutMeta}>
                                <div className={styles.aboutMetaRow}>
                                    <span className={styles.aboutMetaLabel}>Version</span>
                                    <span className={styles.aboutMetaValue}>v{buildInfo.version}</span>
                                </div>
                                <div className={styles.aboutMetaRow}>
                                    <span className={styles.aboutMetaLabel}>Build</span>
                                    <span className={styles.aboutMetaValue}>{buildInfo.buildTime}</span>
                                </div>
                                {updateInfo && updateInfo.newer && (
                                    <div className={`${styles.aboutMetaRow} ${styles.aboutUpdateRow}`}>
                                        <span className={styles.aboutMetaLabel}>{t('menu:about.newVersion')}</span>
                                        <span className={styles.aboutMetaValue}>
                                            v{updateInfo.latest}
                                            {' '}
                                            {/* 외부 링크 — main.js 의 setWindowOpenHandler 가 기본 브라우저로 연다 */}
                                            <a href={(updateInfo.files[0] && updateInfo.files[0].url) || updateInfo.downloadPage} target="_blank" rel="noopener noreferrer">
                                                {t('menu:about.download')}
                                            </a>
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className={styles.aboutSection}>
                                <h3 className={styles.aboutSectionTitle}>{t('menu:about.features')}</h3>
                                <ul className={styles.aboutList}>
                                    <li>{t('menu:about.multiDb')}</li>
                                    <li>{t('menu:about.sessions')}</li>
                                    <li>{t('menu:about.editor')}</li>
                                    <li>{t('menu:about.explorer')}</li>
                                    <li>{t('menu:about.monitoring')}</li>
                                    <li>{t('menu:about.challenge')}</li>
                                </ul>
                            </div>

                            <div className={styles.aboutSection}>
                                <h3 className={styles.aboutSectionTitle}>{t('menu:about.shortcuts')}</h3>
                                <ul className={styles.aboutList}>
                                    <li><kbd>Ctrl+Enter</kbd> {t('menu:about.kbdRun')} <kbd>F10</kbd> {t('menu:about.kbdPlan')}</li>
                                    <li><kbd>Ctrl+S</kbd> {t('menu:about.kbdSave')} <kbd>Ctrl+F</kbd> {t('menu:about.kbdFind')}</li>
                                    <li><kbd>F4</kbd> {t('menu:about.kbdSchema')} <kbd>F2</kbd> {t('menu:about.kbdComments')}</li>
                                </ul>
                            </div>

                            <p className={styles.aboutTagline}>Fun SQL Writing, Fun SQL Tuning!</p>

                            <div className={styles.aboutFooter}>
                                {t('menu:about.footer', { year: new Date().getFullYear() })}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
};

Header.propTypes = {
    isFavoriteAction: PropTypes.func,
    onToggleFavorite: PropTypes.func,
    viewState: PropTypes.object,
    onToggleView: PropTypes.func,
    onOpenCaseList: PropTypes.func,
    onOpenTableAnalysis: PropTypes.func,
    onOpenDataAnalysis: PropTypes.func,
    onOpenDesignGuides: PropTypes.func,
    onOpenOpsLog: PropTypes.func,
    onOpenExperience: PropTypes.func,
    appActionKeys: PropTypes.arrayOf(PropTypes.string),
    activeSession: PropTypes.object,
    activeSessionConnection: PropTypes.object,
    hasActiveTab: PropTypes.bool,
    unsavedTabCount: PropTypes.number,
    onOpenSettings: PropTypes.func,
    onSaveFile: PropTypes.func,
    onOpenFile: PropTypes.func,
    onNewFile: PropTypes.func,
    onSaveProject: PropTypes.func,
    onSaveProjectAs: PropTypes.func,
    currentProjectName: PropTypes.string,
    onGlobalSearch: PropTypes.func,
    onExecuteAll: PropTypes.func,
    onExecuteCurrent: PropTypes.func,
    onExecutePlan: PropTypes.func.isRequired,
    onViewExecutionHistory: PropTypes.func.isRequired,
    onViewSchemaInfo: PropTypes.func.isRequired,
    onCommit: PropTypes.func,
    onRollback: PropTypes.func,
    isEditorFocused: PropTypes.bool,
    onChallengeJoin: PropTypes.func,
    onChallengeTuning: PropTypes.func,
    onChallengeStatus: PropTypes.func,
    screenZoom: PropTypes.number,
    onZoomChange: PropTypes.func,
    onEditorFind: PropTypes.func,
    onEditorReplace: PropTypes.func,
    onEditorFormat: PropTypes.func,
    onEditorUppercase: PropTypes.func,
    onEditorLowercase: PropTypes.func,
    onEditorToggleComment: PropTypes.func,
    onOpenSqlPerfInEditor: PropTypes.func
};

Header.defaultProps = {
    isEditorFocused: false
};

// 편집기에 글자를 칠 때마다 App 이 다시 그려져도 값이 같으면 다시 그리지 않는다(common/memoWindow, 2026-10-26 — 입력 지연)
export default memoWindow(Header); 